import apiService from './api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';
import type { ApiResponse } from '@/types/api';
import type { Notification } from '@/types/api.types';

export class NotificationService {
  /**
   * Получить все уведомления текущего пользователя
   */
  static async getMyNotifications(): Promise<Notification[]> {
    try {
      const { data: response } = await apiService.get<ApiResponse<Notification[]>>(
        API_ENDPOINTS.NOTIFICATIONS.MY_NOTIFICATIONS
      );
      return response.data;
    } catch (error) {
      logger.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Получить количество непрочитанных уведомлений
   */
  static async getUnreadCount(): Promise<number> {
    try {
      const { data: response } = await apiService.get<ApiResponse<{ count: number }>>(
        API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT
      );
      return response.data.count || 0;
    } catch (error) {
      logger.error('Error fetching unread count:', error);
      return 0;
    }
  }

  /**
   * Отметить уведомление как прочитанное
   */
  static async markAsRead(notificationId: number): Promise<void> {
    try {
      // Используем PATCH, но также поддерживаем POST для совместимости
      try {
        await apiService.patch(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId));
      } catch (patchError: any) {
        // Если PATCH не поддерживается (405 Method Not Allowed), пробуем POST
        if (patchError?.response?.status === 405 || patchError?.message?.includes('405')) {
          await apiService.post(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId));
        } else {
          throw patchError;
        }
      }
    } catch (error) {
      logger.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Отметить все уведомления как прочитанные
   */
  static async markAllAsRead(): Promise<void> {
    try {
      // Используем PATCH, но также поддерживаем POST для совместимости
      try {
        await apiService.patch(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);
      } catch (patchError: any) {
        // Если PATCH не поддерживается (405 Method Not Allowed), пробуем POST
        if (patchError?.response?.status === 405 || patchError?.message?.includes('405')) {
          await apiService.post(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);
        } else {
          throw patchError;
        }
      }
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Получить конкретное уведомление
   */
  static async getById(notificationId: number): Promise<Notification> {
    try {
      const { data: response } = await apiService.get<ApiResponse<Notification>>(
        API_ENDPOINTS.NOTIFICATIONS.GET(notificationId)
      );
      return response.data;
    } catch (error) {
      logger.error('Error fetching notification:', error);
      throw error;
    }
  }
}

export default NotificationService;